import { useLoginStore } from '@/login/stores/useLoginStore';
import { storeToRefs } from 'pinia';
import { BankIDStatus, type ReferenceName } from '@sysarb/sysarb-types/services/v1/login';
import { computed, onUnmounted, ref, type ComputedRef, type Ref } from 'vue';
import { useIntervalFn } from '@vueuse/core';
import { getApiHost, getTopDomain } from '@/appSettings';
import { api } from '@/api';
import type { SsoSettingsByUsernameResult } from '@/api/login';
import { QueryClient, useMutation, useQuery } from '@tanstack/vue-query';
import { ResgateManager } from '@/newApi/services/ResgateManager';
import { EventService } from '@/services/EventService';
import { useRoute, useRouter } from 'vue-router';
import { RouteNames } from '@/models/enums/RouteNames';

type LoginManagerReturn = {
    /**
     * Look up the login method for a username. Returns the configured login type
     * and any SSO setups available, or null on failure.
     */
    checkLoginMethod: (username: string) => Promise<SsoSettingsByUsernameResult>;
    /**
     * Redirect the browser to the SSO login url for the given SSO setup id.
     */
    redirectToSso: (id: string) => void;
    /**
     * Login with username and password.s
     * Will change to require SMS if needed.
     */
    loginWithPassword: (username: string, password: string) => Promise<boolean>;
    /**
     * Start the BankID login process.
     */
    loginWithBankId: () => Promise<void>;
    /**
     * Abort the BankID login process.
     */
    abortBankId: () => Promise<void>;
    /**
     * Authenticate the login process with SMS code.
     */
    loginWithSmsCode: (username: string, code: string) => Promise<boolean>;
    /**
     * Current BankID status.
     */
    bankIdStatus: Ref<BankIDStatus>;
    /**
     * Current BankID QR code.
     */
    bankIdQrCode: Ref<string | null>;
    /**
     * Check if the BankID status allows for a retry.
     */
    bankIdCanRetry: ComputedRef<boolean>;
    /**
     * Check if the login process requires an SMS code.
     */
    requireSms: ComputedRef<boolean>;
    /**
     * Link for starting BankID on local device.
     */
    bankIdLink: ComputedRef<string | null>;
    /**
     * Logout the user.
     */
    logout: () => Promise<void>;
    /**
     * Authenticate the user with Resgate after a successful login.
     * This is required to receive real-time updates and use Resgate features.
     */
    loginResgate: () => Promise<void>;
    /**
     * BankID login is in progress.
     */
    bankIdInProgress: ComputedRef<boolean>;
    /** Return to first step in login */
    backToUsername: () => void;
};

export function useLoginManager(): LoginManagerReturn {
    const loginStore = useLoginStore();
    const { isSupport, instance, requireSms } = storeToRefs(loginStore);
    const loginSettingsByUsernameMutation = useMutation(api.login.mutations.loginSettingsByUsername);
    const authPasswordMutation = useMutation(api.login.mutations.authPassword);
    const authSmsCodeMutation = useMutation(api.login.mutations.authSmsCode);
    const startBankIDAuthMutation = useMutation(api.login.mutations.startBankIDAuth);
    const pollBankIDResultMutation = useMutation(api.login.mutations.pollBankIDResult);
    const pollBankIdQrCodeMutation = useMutation(api.login.mutations.pollBankIDQrCode);
    const abortBankIDAuthMutation = useMutation(api.login.mutations.abortBankIDAuth);
    const queryClient = new QueryClient();
    const verifyQuery = useQuery(api.login.queries.validate());
    const router = useRouter();
    const route = useRoute();

    function backToUsername(): void {
        void router.push({ name: RouteNames.Login, query: route.query });
    }

    /*********
     * SSO settings lookup
     *********/
    async function checkLoginMethod(username: string): Promise<SsoSettingsByUsernameResult> {
        loginStore.setLoggingIn(true);
        const result = await loginSettingsByUsernameMutation.mutateAsync({ username });
        loginStore.setLoggingIn(false);
        return result;
    }

    function redirectToSso(id: string): void {
        window.location.assign(`https://${getApiHost()}/sso-service/login?customer=${id}`);
    }

    /*********
     * Password Login
     *********/
    async function loginWithPassword(username: string, password: string): Promise<boolean> {
        // TODO: handle errors, most will require a new token to be generated.
        loginStore.setLoggingIn(true);
        loginStore.setPasswordLoginError(false);

        const result = await authPasswordMutation.mutateAsync({ username, password });

        if (result == null) {
            console.error('LOGIN', 'Failed to authenticate with password');
            await logout();
            loginStore.setLoggingIn(false);
            loginStore.setPasswordLoginError(true);
            return false;
        }

        loginStore.setRequireSms(result.requiresSmsCode);

        if (requireSms.value) {
            loginStore.setLoggingIn(false);
        }

        void verifyQuery.refetch();
        return true;
    }

    /*********
     * SMS Code
     *********/
    async function loginWithSmsCode(username: string, code: string): Promise<boolean> {
        loginStore.setLoggingIn(true);
        loginStore.setSmsLoginError(false);

        const result = await authSmsCodeMutation.mutateAsync({ username, code });
        loginStore.setRequireSms(false);

        if (result === false) {
            console.error('LOGIN', 'Failed to authenticate with SMS code');
            await logout();
            loginStore.setLoggingIn(false);
            loginStore.setSmsLoginError(true);
            return false;
        }

        void verifyQuery.refetch();
        return true;
    }

    /*********
     * Bank ID
     *********/
    const currentBankIdReference = ref<ReferenceName | null>(null);
    const bankIdStatus = ref<BankIDStatus>(BankIDStatus.None);
    const bankIdQrCode = ref<string | null>(null);

    const bankIdCanRetry = computed(() => {
        const notAcceptedStates = [
            BankIDStatus.None,
            BankIDStatus.PendingWait,
            BankIDStatus.Complete,
            BankIDStatus.PendingSign
        ];
        return notAcceptedStates.includes(bankIdStatus.value) === false;
    });

    /**
     * Check for BankID status and update the QR code automatically.
     */
    const pollBankId = useIntervalFn(() => {
        void pollBankIDResult();
        void pollBankIdQrCode();
    }, 1000, { immediate: false });

    async function loginWithBankId(): Promise<void> {
        const bankIdReference = await startBankIDAuthMutation.mutateAsync();
        currentBankIdReference.value = bankIdReference;

        // Poll for the login results every other second
        pollBankId.resume();
    }

    function clearBankId(): void {
        pollBankId.pause();
        currentBankIdReference.value = null;
        bankIdQrCode.value = null;
    }

    async function pollBankIDResult(): Promise<void> {
        if (currentBankIdReference.value == null) return;

        try {
            const payload = { referenceName: currentBankIdReference.value.referenceName };
            const bankIDStatus = await pollBankIDResultMutation.mutateAsync(payload);
            bankIdStatus.value = bankIDStatus ?? BankIDStatus.None;

            switch (bankIDStatus) {
                case BankIDStatus.None:
                case BankIDStatus.PendingWait:
                case BankIDStatus.PendingSign:
                    break;
                case BankIDStatus.Support:
                    clearBankId();
                    setTimeout(() => {
                        window.location.href = `https://system.sysarb.${getTopDomain()}/instances`;
                    }, 100);
                    break;
                default: {
                    clearBankId();
                }
            }

            void verifyQuery.refetch();
        } catch (e: unknown) {
            console.error('LOGIN', 'Failed to poll BankID result', e);
        }
    }

    async function pollBankIdQrCode(): Promise<void> {
        try {
            if (currentBankIdReference.value == null) return;

            const result = await pollBankIdQrCodeMutation.mutateAsync({ referenceName: currentBankIdReference.value.referenceName });
            bankIdQrCode.value = result?.qrCode ?? null;
        } catch (e: unknown) {
            console.error('LOGIN', 'Failed to poll bankID QR code', e);
        }
    }

    async function abortBankId(): Promise<void> {
        if (currentBankIdReference.value != null) {
            try {
                await abortBankIDAuthMutation.mutateAsync({ referenceName: currentBankIdReference.value.referenceName });
            } catch (e: unknown) {
                console.error('LOGIN', 'Failed to abort BankID', e);
                return;
            }
        }

        bankIdQrCode.value = null;
        bankIdStatus.value = BankIDStatus.None;
        currentBankIdReference.value = null;
    }

    const bankIdInProgress = computed(() => currentBankIdReference.value !== null);

    const bankIdLink = computed(() => {
        if (currentBankIdReference.value?.autostartToken == null) return null;
        return `bankid:///?autostarttoken=${currentBankIdReference.value.autostartToken}&redirect=null`;
    });

    async function logout(): Promise<void> {
        console.info('LOGIN MANAGER', 'Logging out');
        void queryClient.cancelQueries({ fetchStatus: 'fetching' });
        queryClient.clear();

        if (loginStore.instance != null) {
            await EventService.getInstance(router).unsubscribeAll();
        }

        if (isSupport.value) {
            loginStore.setInstance(null);

            // Important note!
            // This will not work if the window was not opened by the system portal.
            // If you open a new tab manually and paste the URL, the window will not close.
            window.close();
            return;
        }

        await queryClient.fetchQuery(api.login.queries.invalidate()).finally(() => {
            void queryClient.fetchQuery(api.login.queries.validate(true));
        });
    }

    async function loginResgate(): Promise<void> {
        loginStore.setResgateAuthenticated(false);

        if (!ResgateManager.hasClient) return;

        const authenticatingInstance = instance.value;

        await ResgateManager.client.authenticate('sysarb.login', 'cookietoken', {
            subdomain: authenticatingInstance ?? ''
        }).then(() => {
            loginStore.setResgateAuthenticated(true);
            loginStore.setLastAuthenticatedInstance(authenticatingInstance);
        }).catch((e: unknown) => {
            console.error(e);
            void logout();
        });
    }

    onUnmounted(() => {
        pollBankId.pause();
        bankIdQrCode.value = null;
        bankIdStatus.value = BankIDStatus.None;
        currentBankIdReference.value = null;
    });

    return {
        checkLoginMethod,
        redirectToSso,
        loginWithPassword,
        loginWithBankId,
        abortBankId,
        loginWithSmsCode,
        bankIdStatus,
        bankIdQrCode,
        bankIdCanRetry,
        requireSms,
        bankIdLink,
        logout,
        loginResgate,
        bankIdInProgress,
        backToUsername
    };
}
